import { useEffect, useState, useCallback } from "react";
import {
  DndContext,
  DragEndEvent,
  DragOverEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { GroupLeadCard } from "./GroupLeadCard";
import { supabase } from "@/integrations/supabase/client";
import { removeSupabaseChannelByName, subscribePostgresChannel } from "@/lib/realtime-channel";
import { bindRealtimeVisibilityPause } from "@/lib/realtime-visibility-pause";
import { REALTIME_PAUSE_ON_HIDDEN_ENABLED } from "@/lib/workspace-conversation-realtime-hub";
import { useToast } from "@/hooks/use-toast";
import type { RealtimeChannel } from "@supabase/supabase-js";
import { cn } from "@/lib/utils";
import { useGroupStages, type GroupStage } from "@/hooks/useGroupStages";

const ORDER_GAP = 1000;

interface Lead {
  id: string;
  name: string;
  email?: string;
  phone?: string;
  stage: string;
  notes?: string;
  assigned_agent_id?: string;
  created_at: string;
}

interface GroupMembershipWithLead {
  id: string;
  lead_id: string;
  group_id: string;
  group_stage_id: string | null;
  role?: string;
  department?: string;
  order_index?: number;
  lead: Lead;
}

interface GroupsKanbanProps {
  groupId: string;
  workspaceId: string;
  onLeadClick: (lead: Lead) => void;
}

function SortableMembershipCard({
  membership,
  onLeadClick,
}: {
  membership: GroupMembershipWithLead;
  onLeadClick: (lead: Lead) => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
    isOver,
  } = useSortable({ id: membership.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      {isOver && (
        <div className="h-0.5 bg-primary rounded-full mx-2 mb-1 animate-in fade-in duration-200" />
      )}
      <div className={cn(isDragging && "opacity-30")}>
        <GroupLeadCard
          membershipId={membership.id}
          lead={membership.lead}
          role={membership.role}
          department={membership.department}
          onClick={() => onLeadClick(membership.lead)}
        />
      </div>
    </div>
  );
}

function DroppableColumn({
  stage,
  memberships,
  onLeadClick,
}: {
  stage: GroupStage;
  memberships: GroupMembershipWithLead[];
  onLeadClick: (lead: Lead) => void;
}) {
  const { setNodeRef, isOver } = useDroppable({
    id: stage.id,
  });

  return (
    <Card
      ref={setNodeRef}
      className={cn(
        "flex flex-col h-full min-h-[600px] transition-colors w-[280px] flex-shrink-0",
        isOver && "ring-2 ring-primary"
      )}
    >
      <CardHeader className="pb-3 flex-shrink-0 px-4 pt-4 border-b">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <div className={`w-2 h-2 rounded-full ${stage.color} flex-shrink-0`} />
          <span className="truncate">{stage.name}</span>
          <Badge variant="secondary" className="ml-auto text-xs flex-shrink-0">
            {memberships.length}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="flex-1 overflow-y-auto space-y-2 px-4 py-4">
        <SortableContext
          items={memberships.map((m) => m.id)}
          strategy={verticalListSortingStrategy}
        >
          {memberships.map((membership) => (
            <SortableMembershipCard
              key={membership.id}
              membership={membership}
              onLeadClick={onLeadClick}
            />
          ))}
        </SortableContext>
        {memberships.length === 0 && (
          <>
            {isOver && (
              <div className="h-0.5 bg-primary rounded-full mx-2 mb-1 animate-in fade-in duration-200" />
            )}
            <p className="text-sm text-muted-foreground text-center py-8">
              Nenhum lead nesta coluna
            </p>
          </>
        )}
      </CardContent>
    </Card>
  );
}

export function GroupsKanban({
  groupId,
  workspaceId,
  onLeadClick,
}: GroupsKanbanProps) {
  const { stages, loading: stagesLoading } = useGroupStages(groupId);
  const [memberships, setMemberships] = useState<GroupMembershipWithLead[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState<string | null>(null);
  const { toast } = useToast();

  const fetchMemberships = useCallback(async () => {
    if (!groupId) return;

    try {
      setLoading(true);
      const { data, error } = await supabase
        .from("lead_group_memberships")
        .select(`
          id,
          lead_id,
          group_id,
          group_stage_id,
          role,
          department,
          order_index,
          lead:lead_id (
            id,
            name,
            email,
            phone,
            stage,
            notes,
            assigned_agent_id,
            created_at
          )
        `)
        .eq("group_id", groupId);

      if (error) throw error;

      const validMemberships = (data || [])
        .filter((m: any) => m.lead)
        .map((m: any) => ({
          id: m.id,
          lead_id: m.lead_id,
          group_id: m.group_id,
          group_stage_id: m.group_stage_id,
          role: m.role,
          department: m.department,
          order_index: m.order_index || 0,
          lead: m.lead,
        }));

      setMemberships(validMemberships);
    } catch (error: any) {
      console.error("Error fetching group memberships:", error);
      toast({
        title: "Erro ao carregar leads do grupo",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [groupId, toast]);

  useEffect(() => {
    fetchMemberships();
  }, [fetchMemberships]);

  // Realtime subscription para memberships
  useEffect(() => {
    if (!groupId) return;

    const channelName = `group-memberships-${groupId}`;
    let channel: RealtimeChannel | null = null;
    const reconcileTimerRef = { current: null as ReturnType<typeof setTimeout> | null };

    const subscribeChannel = () => {
      if (REALTIME_PAUSE_ON_HIDDEN_ENABLED && typeof document !== "undefined" && document.hidden) {
        return;
      }
      if (channel) return;
      channel = subscribePostgresChannel(
        channelName,
        {
          event: "*",
          schema: "public",
          table: "lead_group_memberships",
          filter: `group_id=eq.${groupId}`,
        },
        () => {
          fetchMemberships();
        },
      );
    };

    const teardownChannel = () => {
      if (!channel) return;
      removeSupabaseChannelByName(channelName);
      supabase.removeChannel(channel);
      channel = null;
    };

    const reconcileMemberships = () => {
      if (reconcileTimerRef.current) clearTimeout(reconcileTimerRef.current);
      reconcileTimerRef.current = setTimeout(() => {
        fetchMemberships();
      }, 300);
    };

    const unbind = bindRealtimeVisibilityPause({
      pauseEnabled: REALTIME_PAUSE_ON_HIDDEN_ENABLED,
      subscribe: subscribeChannel,
      teardown: teardownChannel,
      onReconcile: reconcileMemberships,
    });

    return () => {
      unbind();
      if (reconcileTimerRef.current) clearTimeout(reconcileTimerRef.current);
    };
  }, [groupId, fetchMemberships]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  };

  const handleDragOver = (event: DragOverEvent) => {
    // Visual feedback handled by SortableContext
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);

    if (!over) return;

    const membershipId = active.id as string;
    const membership = memberships.find((m) => m.id === membershipId);
    if (!membership) return;

    // Determine target stage
    const isStageId = stages.some((s) => s.id === over.id);
    let targetStageId: string;
    let overMembershipId: string | null = null;

    if (isStageId) {
      targetStageId = over.id as string;
    } else {
      // Dropped over another membership card
      const overMembership = memberships.find((m) => m.id === over.id);
      targetStageId = overMembership?.group_stage_id || stages[0]?.id || "";
      overMembershipId = over.id as string;
    }

    if (!targetStageId) return;

    const previousStageId = membership.group_stage_id;
    const stageChanged = previousStageId !== targetStageId;
    const previousOrderIndex = membership.order_index || 0;

    // Get memberships in target stage, sorted by order_index
    const stageIds = stages.map((s) => s.id);
    const targetMemberships = memberships
      .filter((m) => {
        const mStageId = m.group_stage_id && stageIds.includes(m.group_stage_id)
          ? m.group_stage_id
          : stages[0]?.id;
        return mStageId === targetStageId && m.id !== membershipId;
      })
      .sort((a, b) => (a.order_index || 0) - (b.order_index || 0));

    // Determine insertion index
    let insertIndex = targetMemberships.length;
    if (overMembershipId) {
      const overIndex = targetMemberships.findIndex(
        (m) => m.id === overMembershipId
      );
      if (overIndex >= 0) {
        insertIndex = overIndex;
      }
    }

    const prevNeighbor = insertIndex > 0 ? targetMemberships[insertIndex - 1] : null;
    const nextNeighbor = insertIndex < targetMemberships.length ? targetMemberships[insertIndex] : null;
    const prevOrder = prevNeighbor?.order_index ?? null;
    const nextOrder = nextNeighbor?.order_index ?? null;

    let newOrderIndex: number | null = null;
    let needsNormalize = false;

    if (prevOrder == null && nextOrder == null) {
      newOrderIndex = ORDER_GAP;
    } else if (prevOrder == null && nextOrder != null) {
      newOrderIndex = Number(nextOrder) - ORDER_GAP;
    } else if (prevOrder != null && nextOrder == null) {
      newOrderIndex = Number(prevOrder) + ORDER_GAP;
    } else if (prevOrder != null && nextOrder != null) {
      const gap = Number(nextOrder) - Number(prevOrder);
      if (gap > 1) newOrderIndex = Math.floor((Number(prevOrder) + Number(nextOrder)) / 2);
      else needsNormalize = true;
    }

    if (needsNormalize) {
      let normalizedOk = false;
      try {
        const includeUnassigned = targetStageId === stages[0]?.id;
        const { error } = await (supabase as any).rpc("normalize_group_memberships_stage_order", {
          _group_id: groupId,
          _target_stage_id: targetStageId,
          _gap: ORDER_GAP,
          _include_unassigned: includeUnassigned,
        });
        normalizedOk = !error;
      } catch (e) {
        console.error("❌ Failed to normalize group order_index:", e);
      }

      if (!normalizedOk) {
        // Fallback seguro: mantém funcionalidade sem storm (sem Promise.all).
        const reordered = [...targetMemberships];
        reordered.splice(insertIndex, 0, { ...membership, group_stage_id: targetStageId });
        try {
          for (let idx = 0; idx < reordered.length; idx++) {
            const { error } = await supabase
              .from("lead_group_memberships")
              .update({ group_stage_id: targetStageId, order_index: idx })
              .eq("id", reordered[idx].id);
            if (error) throw error;
          }
          fetchMemberships();
          return;
        } catch (error: any) {
          fetchMemberships();
          toast({
            title: "Erro ao mover lead",
            description: error.message,
            variant: "destructive",
          });
          return;
        }
      }

      const n = targetMemberships.length;
      if (insertIndex === 0) newOrderIndex = Math.floor(ORDER_GAP / 2);
      else if (insertIndex === n) newOrderIndex = (n + 1) * ORDER_GAP;
      else newOrderIndex = insertIndex * ORDER_GAP + Math.floor(ORDER_GAP / 2);
    }

    if (newOrderIndex == null) return;

    // Optimistic update (apenas o item movido)
    setMemberships((prev) =>
      prev.map((m) =>
        m.id === membershipId ? { ...m, group_stage_id: targetStageId, order_index: newOrderIndex } : m,
      ),
    );

    try {
      const { error } = await supabase
        .from("lead_group_memberships")
        .update({ group_stage_id: targetStageId, order_index: newOrderIndex })
        .eq("id", membershipId);
      if (error) throw error;
    } catch (error: any) {
      // Rollback
      setMemberships((prev) =>
        prev.map((m) =>
          m.id === membershipId ? { ...m, group_stage_id: previousStageId, order_index: previousOrderIndex } : m,
        ),
      );
      fetchMemberships();
      toast({
        title: "Erro ao mover lead",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const getMembershipsByStage = (stageId: string) => {
    return memberships
      .filter((m) => m.group_stage_id === stageId)
      .sort((a, b) => (a.order_index || 0) - (b.order_index || 0));
  };

  const getUnassignedMemberships = () => {
    const stageIds = stages.map((s) => s.id);
    return memberships
      .filter((m) => !m.group_stage_id || !stageIds.includes(m.group_stage_id))
      .sort((a, b) => (a.order_index || 0) - (b.order_index || 0));
  };

  const activeMembership = memberships.find((m) => m.id === activeId);

  if (stagesLoading || loading) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        Carregando colunas do grupo...
      </div>
    );
  }

  if (stages.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        Nenhuma coluna configurada para este grupo.
        <br />
        Use o botão "Colunas" para criar as colunas do Kanban.
      </div>
    );
  }

  const unassigned = getUnassignedMemberships();

  return (
    <DndContext
      sensors={sensors}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
    >
      <div className="h-full w-full overflow-x-auto overflow-y-auto scrollbar-thin scrollbar-thumb-primary/20 scrollbar-track-transparent">
        <div
          className="flex gap-4 h-full"
          style={{
            minWidth: `${stages.length * 280}px`,
            width: "max-content",
          }}
        >
          {stages.map((stage, index) => {
            let stageMemberships = getMembershipsByStage(stage.id);
            if (index === 0) {
              stageMemberships = [...stageMemberships, ...unassigned];
            }

            return (
              <DroppableColumn
                key={stage.id}
                stage={stage}
                memberships={stageMemberships}
                onLeadClick={onLeadClick}
              />
            );
          })}
        </div>
      </div>

      <DragOverlay>
        {activeMembership ? (
          <div className="rotate-3 opacity-80">
            <GroupLeadCard
              membershipId={activeMembership.id}
              lead={activeMembership.lead}
              role={activeMembership.role}
              department={activeMembership.department}
              onClick={() => {}}
            />
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}
